import React, { PureComponent } from 'react';
import cn from 'classnames';
import { FormGroup, FormText, Col, Label } from 'reactstrap';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { string, func } from 'prop-types';
import { req, webmaster } from '../../services/ApiService.js';
import ProfileAvatarImg from './ProfileAvatarImg';

const ALLOWED_EXTENSIONS = /(\.jpg|\.jpeg|\.png)$/i; // JPEG, PNG
const MAX_FILE_SIZE = 10; // MB
const REQUEST_ID = 'WEBMASTER_AVATAR_UPLOAD';

class ProfileAvatar extends PureComponent {
  static propTypes = {
    intl: intlShape.isRequired,
    avatar: string,
    onFileUpload: func,
  };

  constructor(props) {
    super(props);

    this.state = {
      upload: false,
      completed: 0,
      error: '',
      avatar: props.avatar ? props.avatar : '',
    };

    this.$el = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { avatar } = nextProps;

    if (avatar !== this.props.avatar) {
      this.setState(() => ({
        avatar,
      }));
    }
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    req.cancel(REQUEST_ID);
  }

  resetInput = () => {
    this._mounted && (this.$el.current.value = '');
  };

  handleChange = ({ target }) => {
    const file = target.files[0];

    if (file) {
      const formData = new FormData();
      const { intl } = this.props;
      const { name, size } = file;

      // Checking file extension
      if (!ALLOWED_EXTENSIONS.exec(name)) {
        this.setState(() => ({
          error: intl.formatMessage({
            id: 'profile.error.file-extension',
            defaultMessage: 'Invalid file extension',
          }),
        }));

        this.resetInput();
        return;
      }

      // Checking file size
      if (Math.round(size / 1024 / 1024) >= MAX_FILE_SIZE) {
        this.setState(() => ({
          error: intl.formatMessage({
            id: 'profile.error.filesize',
            defaultMessage: 'The file size exceeds the limit allowed and cannot be saved',
          }),
        }));

        this.resetInput();
        return;
      }

      formData.append('avatar', file, file.name);

      this.setState(() => ({ error: '', completed: 0, upload: true }));

      webmaster
        .setAvatar(formData, {
          requestId: REQUEST_ID,
          onUploadProgress: ({ loaded, total }) => {
            const completed = Math.round((loaded * 100) / total);
            this.setState(() => ({ completed }));
          },
        })
        .then(({ items = [] }) => {
          if (items.length > 0) {
            const avatar = items[0];
            this.setState(
              () => ({ upload: false, completed: 0, avatar }),
              () => {
                this.props.onFileUpload(avatar);
              },
            );
          }
        })
        .catch(({ response }) => {
          if (response && response.data && response.data.error) {
            const { message: error } = response.data.error;

            this.setState(() => ({
              upload: false,
              completed: 0,
              error: error.message,
            }));
          } else {
            this.setState(() => ({ upload: false }));
          }

          this.resetInput();
        });
    }
  };

  render() {
    const { error, upload, avatar, completed } = this.state;

    return (
      <FormGroup className={cn('profile-avatar', { 'has-error': error })}>
        <ProfileAvatarImg upload={upload} avatar={avatar} completed={completed} />
        <div className="file-wrapper">
          <Label htmlFor="avatar">
            <input
              className="hidden sr-only"
              type="file"
              id="avatar"
              type="file"
              accept="image/x-png, image/jpeg"
              ref={this.$el}
              onChange={this.handleChange}
            />
            <span className={cn('btn btn-sm form-group-btn', { disabled: upload })}>
              <FormattedMessage id="main.upload" defaultMessage="Upload" />
            </span>
          </Label>
          <FormText>
            <div>
              <FormattedMessage id="profile.avatar_format" defaultMessage="JPG, PNG" />
            </div>
            <div>
              <FormattedMessage id="profile.avatar_max" defaultMessage="max size - 10 MB" />
            </div>
          </FormText>
        </div>
        {error && <small className="form-text">{error}</small>}
      </FormGroup>
    );
  }
}

export default injectIntl(ProfileAvatar);
